import React, { useEffect, useRef, useState } from 'react';
import snsWebSdk from '@sumsub/websdk';
import { CloseButtonSvgIcon } from './common/IconProvider';
import KycService from '../services/kyc.service';
import { Alert } from '../services/notiflix.service';
import { useDispatch, useSelector } from 'react-redux';
import { updateAuthUser } from '../actions/auth';

const KycVerification = ({ modelClose, onSuccess, nextStep }) => {
  const [kycDetails, setKycDetails] = useState({ sdkToken: null, id: null });
  const language = useSelector((state) => state.languageReducer.language);
  const widgetRef = useRef(null);
  const [kycReport, setKycReport] = useState(null);
  const statusCheckInterval = useRef(null);
  const { user: authUser } = useSelector((state) => state.auth);
  const [webSdkInstance, setWebSdkInstance] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    initiateKyc();
  }, []);

  const initiateKyc = async () => {
    try {
      let tokenRes = await KycService.initiateKyc();
      if (tokenRes.status === 'failure') {
        modelClose();
        Alert.error(tokenRes.message);
        return false;
      }
      setKycDetails(tokenRes.data);
      let updatedAuthUser = { ...authUser };
      updatedAuthUser['is_kyc_consent'] = tokenRes.data.is_kyc_consent;
      dispatch(updateAuthUser(updatedAuthUser));
    } catch (error) {
      modelClose();
      Alert.error(error.message);
      return false;
    }
  };

  const checkKycProgress = async (_data) => {
    try {
      let resp = await KycService.checkKycProgress(_data);

      if (resp.status === 'failure') {
        Alert.error(resp.message);
        modelClose();
        return false;
      }

      setKycReport(resp.data);
      if (
        resp.data.status === 3 ||
        resp.data.status === 4 ||
        resp.data.status === 5
      ) {
        ('Failed to reset application');
        let updatedAuthUser = { ...authUser };
        updatedAuthUser['kyc_status'] = resp.data.user.kyc_status;
        dispatch(updateAuthUser(updatedAuthUser));
        statusCheckInterval.current &&
          clearInterval(statusCheckInterval.current);
        statusCheckInterval.current = null;
        return false;
      }

      if (statusCheckInterval.current == null) {
        statusCheckInterval.current = setInterval(async () => {
          await checkKycProgress({ id: _data.id });
        }, 5000);
      }
    } catch (error) {
      modelClose();
      Alert.error(error.message);
    }
  };

  useEffect(() => {
    if (kycDetails.sdkToken) {
      const snsWebSdkInstance = snsWebSdk
        .init(
          kycDetails.sdkToken,
          // token update callback, must return Promise
          () => Promise.resolve(kycDetails.sdkToken),
        )
        .withConf({
          lang: language, // language of WebSDK texts and comments (ISO 639-1 format)
          theme: 'light',
        })
        .on('idCheck.onError', (error) => {
          Alert.error(error.message);
        })
        .on('idCheck.onApplicantStatusChanged', (payload) => {
          if (
            payload.reviewResult &&
            payload.reviewResult.reviewAnswer === 'RED' &&
            payload.reviewResult.reviewRejectType === 'FINAL'
          )
            if (
              payload.reviewResult &&
              payload.reviewResult.reviewAnswer === 'RED'
            ) {
              // Equivalent to onComplete
              checkKycProgress({ id: kycDetails.id });
            }
          if (
            payload.reviewResult &&
            payload.reviewResult.reviewAnswer === 'GREEN'
          ) {
            // Equivalent to onComplete
            checkKycProgress({ id: kycDetails.id });
            // Delay closing to allow message display
            setTimeout(() => {
              closeKycModal();
            }, 3000);
          }
        })
        .onMessage(() => {
          checkKycProgress({ id: kycDetails.id });
        })
        .build();

      snsWebSdkInstance.launch('#sumsub-websdk-container'); // Ensure this matches the ID below
      setWebSdkInstance(snsWebSdkInstance);
    }
  }, [kycDetails.sdkToken]);

  const closeKycModal = async () => {
    statusCheckInterval.current && clearInterval(statusCheckInterval.current);
    statusCheckInterval.current = null;
    modelClose();
    webSdkInstance?.destroy();
  };

  const handleKycSuccess = () => {
    if (onSuccess) {
      onSuccess(); // Call the success callback
    }
  };

  return (
    <>
      <>
        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none sm:my-10 md:my-6">
          <div className="relative w-full md:w-auto mx-3 my-4">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full dark:bg-slate-800  dark:text-slate-200  bg-white outline-none focus:outline-none md:max-w-[524px] md:min-w-[512px] md:min-h-[470px] ">
              {/*header*/}
              <button
                className="bg-transparent border-0 text-black text-2xl leading-none font-semibold outline-none focus:outline-none absolute right-1 top-1 z-10 flex justify-center items-center"
                onClick={() => {
                  closeKycModal();
                }}
              >
                <span className="bg-white text-black w-10 dark:rounded-md block outline-none focus:outline-none hover:bg-gray-300">
                  <CloseButtonSvgIcon />
                </span>
              </button>

              {kycReport && kycReport?.status === 3 ? (
                <div className="relative p-6 flex-auto flex items-center justify-center flex-col">
                  <div className=" py-2 px-6 text-center w-full">
                    <button
                      onClick={() => {
                        closeKycModal();
                        handleKycSuccess();
                      }}
                      className="bg-GreenColor active:bg-[#92C24B] text-brandBlue border border-GreenColor font-bold uppercase text-sm px-6 py-3 rounded-lg w-auto inline-block shadow hover:shadow-lg outline-none focus:outline-none  mb-1 ease-linear transition-all duration-150"
                      type="button"
                    >
                      {nextStep ? 'Continue' : 'Close'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  id="sumsub-websdk-container"
                  ref={widgetRef}
                  style={{
                    minHeight: '80vh',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    width: '100%',
                    position: 'relative',
                  }}
                  className="relative p-6 pt-10 flex-auto"
                ></div>
              )}

              <div></div>
            </div>
          </div>
        </div>
      </>
    </>
  );
};

export default KycVerification;
